// Top Qualifier Hosted Chat Page
// Public-facing page at /top-qualifier/:slug
// Displays TQ chat interface for leads to interact with

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabase/client';

interface TQTenant {
  tq_tenant_id: string;
  tq_customer_slug: string;
  embed_key: string;
  status: string;
  company_id: string;
}

interface CompanyInfo {
  name: string;
  domain?: string;
}

const TopQualifierHostedChat: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [tenant, setTenant] = useState<TQTenant | null>(null);
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('Invalid chat link');
      setLoading(false);
      return;
    }

    fetchTenantBySlug();
  }, [slug]);

  const fetchTenantBySlug = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch tenant info by slug (public read access via RLS)
      const { data: tenantData, error: tenantError } = await supabase
        .from('tq_tenants')
        .select('tq_tenant_id, tq_customer_slug, embed_key, status, company_id')
        .eq('tq_customer_slug', slug)
        .single();

      if (tenantError) {
        if (tenantError.code === 'PGRST116') {
          setError('Chat not found. Please check the link and try again.');
        } else {
          throw tenantError;
        }
        return;
      }

      if (tenantData.status !== 'active') {
        setError('This chat is currently unavailable. Please contact the company directly.');
        return;
      }

      setTenant(tenantData);

      // Fetch company info for branding
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('name, domain')
        .eq('id', tenantData.company_id)
        .single();

      if (!companyError && companyData) {
        setCompany(companyData);
      }

    } catch (err) {
      console.error('Error fetching tenant:', err);
      setError('Failed to load chat. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load TQ embed script once tenant is loaded
    if (!tenant) return;

    const script = document.createElement('script');
    script.src = 'https://top-qualifier-api.onrender.com/embed/tq.js';
    script.setAttribute('data-tq-key', tenant.embed_key);
    script.setAttribute('data-tq-position', 'center'); // Center for hosted page
    script.setAttribute('data-tq-fullscreen', 'true'); // Make it fullscreen
    script.async = true;

    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      document.body.removeChild(script);
    };
  }, [tenant]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Chat Unavailable</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header Bar */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {company?.name ? company.name.charAt(0).toUpperCase() : 'TQ'}
                </span>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  {company?.name || 'Top Qualifier Chat'}
                </h1>
                <p className="text-sm text-gray-500">AI-powered qualification assistant</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                Online
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ minHeight: 'calc(100vh - 200px)' }}>
          {/* TQ Chat Widget will be injected here by the embed script */}
          <div id="tq-chat-container" className="w-full h-full">
            {/* Loading placeholder while script initializes */}
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="animate-pulse text-4xl mb-4">🤖</div>
                <p className="text-gray-600">Initializing AI assistant...</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Powered by <span className="font-semibold text-purple-600">Top Qualifier AI</span>
            {company?.domain && (
              <>
                {' '} | Hosted by <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">{company.name}</a>
              </>
            )}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Your conversation is encrypted and handled in accordance with our privacy policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopQualifierHostedChat;
